import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import ReactSelect, { formatDotStyles } from '../ReactSelect';
import { format, parseISO, subMonths } from 'date-fns';
import useMode from '../../hooks/useMode';
import { PortalWithState } from 'react-portal';
import { DropdownIndicatorProps } from 'react-select';
import ReactSelectButton from '../ReactSelect/ReactSelectButton';
import GaugeEditModal from './GaugeEditModal';
import { gql as individual_gql } from '@/modes/individual/gql';
import { gql as company_gql } from '@/modes/company/gql';
import { gql as waterSupplier_gql } from '@/modes/waterSupplier/gql';

export const GET_GAUGES_COMPANY = company_gql(`#graphql
  query GetGaugesAsCompany($where: JSON, $orderBy: [ColumnOrder]) {
    vendors(where: $where, orderBy: $orderBy, limit: null) {
      id
      profileId
      profile(required: true) {
        id
        backflowTester {
          gauges(required: true, limit: null) {
            id
            gaugeNumber
            calibrationDate
            isDefault
            archived
          }
        }
      }
    }
  }
`);

export const GET_GAUGES_WATER_SUPPLIER = waterSupplier_gql(`#graphql
  query GetGaugesAsWaterSupplier($where: JSON, $orderBy: [ColumnOrder]) {
    vendors(where: $where, orderBy: $orderBy, limit: null) {
      id
      profileId
      profile(required: true) {
        id
        backflowTester {
          gauges(required: true, limit: null) {
            id
            gaugeNumber
            calibrationDate
            isDefault
            archived
          }
        }
      }
      vendors(limit: null) {
        status
        id
        profileId
        profile(required: true) {
          id
          backflowTester {
            gauges(required: true, limit: null) {
              id
              gaugeNumber
              calibrationDate
              isDefault
              archived
            }
          }
        }
      }
    }
  }
`);

export const GET_GAUGES_INDIVIDUAL = individual_gql(`#graphql
  query GetGaugesAsIndividual {
    me {
      id
      backflowTester {
        gauges(required: true, limit: null) {
          id
          gaugeNumber
          calibrationDate
          isDefault
          archived
        }
      }
    }
  }
`);

export type Gauge = {
  id: string;
  gaugeNumber: string | null;
  calibrationDate: string;
  isDefault: boolean;
  archived?: number | null;
};

type GaugePickerPickerProps = {
  testerProfileId: string;
  companyProfileId?: string;
  gaugeId?: string | number;
  onChange: (gauge: Gauge | null) => void;
  placeholder?: string;
  allowCreate?: boolean;
};

const GaugePicker = ({
  testerProfileId,
  companyProfileId,
  gaugeId,
  onChange,
  placeholder,
  allowCreate,
}: GaugePickerPickerProps): JSX.Element => {
  const mode = useMode();

  const { data: dataIndividual, refetch: refetchIndividual } = useQuery(
    GET_GAUGES_INDIVIDUAL,
    {
      fetchPolicy: 'network-only',
      variables: {
        limit: null,
      },
      skip: mode !== 'individual',
    }
  );

  const { data: dataCompany, refetch: refetchCompany } = useQuery(
    GET_GAUGES_COMPANY,
    {
      fetchPolicy: 'network-only',
      variables: {
        where: {
          profileId: testerProfileId,
        },
        orderBy: [
          {
            col: 'profile.backflowTester.gauges.calibrationDate',
            dir: 'DESC',
          },
        ],
      },
      skip: mode !== 'company',
    }
  );

  const { data: dataWS, refetch: refetchWS } = useQuery(
    GET_GAUGES_WATER_SUPPLIER,
    {
      fetchPolicy: 'network-only',
      variables: {
        where: {
          profileId: companyProfileId || testerProfileId,
          'vendors.profile.id': companyProfileId ? testerProfileId : undefined,
        },
        orderBy: [
          {
            col: companyProfileId
              ? 'vendors.profile.backflowTester.gauges.calibrationDate'
              : 'profile.backflowTester.gauges.calibrationDate',
            dir: 'DESC',
          },
        ],
      },
      skip: mode !== 'water-supplier',
    }
  );

  const refetch =
    mode === 'individual'
      ? refetchIndividual
      : mode === 'company'
        ? refetchCompany
        : mode === 'water-supplier'
          ? refetchWS
          : () => {};

  const gauges: Gauge[] =
    (dataWS
      ? companyProfileId
        ? dataWS.vendors[0]?.vendors[0]?.profile?.backflowTester?.gauges
        : dataWS.vendors[0]?.profile?.backflowTester?.gauges
      : dataCompany
        ? dataCompany.vendors[0]?.profile?.backflowTester?.gauges
        : dataIndividual
          ? dataIndividual.me?.backflowTester?.gauges
          : []) ?? [];

  const selectedGauge: Gauge | undefined = useMemo(
    () => gauges.find((gauge) => gauge.id == gaugeId),
    [gauges, gaugeId]
  );

  const availableGauges: Gauge[] = useMemo(() => {
    if (gauges.length === 0) {
      return [];
    }
    return gauges.filter(
      (gauge) =>
        !gauge.archived &&
        subMonths(Date.now(), 18).getTime() <
          parseISO(gauge.calibrationDate).getTime()
    );
  }, [gauges]);

  useEffect(() => {
    let gauge: Gauge | undefined = selectedGauge;

    if (gaugeId && gauges.length > 0 && !selectedGauge) {
      console.warn('Clearing not found gauge');
      gauge = undefined;
    }
    if (availableGauges.length > 0 && !gauge) {
      const defaultGauge = availableGauges.find((gauge) => gauge.isDefault);
      if (defaultGauge) {
        console.log('No gauge selected. Using default gauge');
        gauge = defaultGauge;
      } else {
        console.log('No gauge selected. Using first in list');
        gauge = availableGauges[0];
      }
      if (gauge?.id !== (gaugeId || undefined)) {
        // can't call setState for parent component in render
        setTimeout(() => {
          onChange(gauge ?? null);
        });
      }
    }
  }, [gaugeId, gauges, availableGauges, selectedGauge]);

  if (selectedGauge && !availableGauges.includes(selectedGauge)) {
    availableGauges.push(selectedGauge);
  }

  const assignGauge = async (gauge: Gauge) => {
    await refetch();
    onChange(gauge);
  };

  return (
    <PortalWithState closeOnEsc>
      {({ openPortal, closePortal, portal, isOpen }) => {
        return (
          <>
            <ReactSelect
              value={selectedGauge}
              options={availableGauges}
              noOptionsMessage={() => null}
              menuPlacement="auto"
              components={{
                DropdownIndicator: (
                  props: PropsWithChildren<DropdownIndicatorProps<Gauge, false>>
                ) => {
                  return allowCreate ? (
                    <ReactSelectButton
                      onClick={openPortal}
                      title="Add new gauge"
                    />
                  ) : null;
                },
              }}
              styles={{
                menu: (base) => ({
                  ...base,
                  minWidth: '250px',
                }),
              }}
              onChange={(selected: Gauge | null) => {
                if (!selected) {
                  onChange(null);
                } else {
                  onChange(selected);
                }
              }}
              placeholder={placeholder ?? 'Select gauge...'}
              getOptionLabel={(gauge: Gauge) =>
                `${gauge.gaugeNumber} (${format(
                  parseISO(gauge.calibrationDate),
                  'M/d/yyyy'
                )})`
              }
              getOptionValue={(gauge: Gauge) => gauge.id}
            />
            {portal(
              <GaugeEditModal
                action="create"
                gauge={{
                  profileId: testerProfileId,
                }}
                onClose={closePortal}
                assignGauge={assignGauge}
              />
            )}
          </>
        );
      }}
    </PortalWithState>
  );
};

export default GaugePicker;
